import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { FAQS } from "../../lib/content";
import { useLang } from "../../lib/i18n";

const FaqItem = ({ item, index, open, onToggle, lang }) => (
  <div className="border-t border-white/12">
    <button
      data-testid={`faq-question-${index}`}
      onClick={onToggle}
      aria-expanded={open}
      className="group w-full flex items-center justify-between gap-6 py-6 md:py-7 text-left"
    >
      <span className="flex items-baseline gap-5">
        <span className="font-serif-display text-lg md:text-xl text-gold/60 tabular-nums group-hover:text-gold transition-colors duration-500">
          0{index + 1}
        </span>
        <span
          className={`font-serif-display text-xl md:text-2xl font-light transition-colors duration-400 ${
            open ? "text-gold" : "text-white group-hover:text-gold"
          }`}
        >
          {lang === "it" ? item.q : item.qEn}
        </span>
      </span>
      <motion.span
        animate={{ rotate: open ? 45 : 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className={`shrink-0 flex h-9 w-9 items-center justify-center rounded-full border transition-colors duration-400 ${
          open ? "border-gold text-gold" : "border-white/25 text-white/60 group-hover:border-gold group-hover:text-gold"
        }`}
      >
        <Plus size={16} />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          data-testid={`faq-answer-${index}`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <p className="pb-7 pl-0 md:pl-12 pr-4 md:pr-16 font-sans-ui font-light text-white/65 leading-relaxed max-w-3xl">
            {lang === "it" ? item.a : item.aEn}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const Faq = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const { lang, tr } = useLang();

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative bg-ad-surface py-24 md:py-40"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <Reveal className="mb-14 md:mb-20">
          <Overline>{tr("Domande & Risposte", "Questions & Answers")}</Overline>
          <h2 className="mt-6 font-serif-display font-light text-4xl md:text-6xl leading-none tracking-tight text-white">
            {tr("Domande ", "Frequently Asked ")}<span className="italic text-gold">{tr("Frequenti", "Questions")}</span>
          </h2>
          <p className="mt-6 font-sans-ui font-light text-white/60 leading-relaxed max-w-xl">
            {tr(
              "Tutto quello che desideri sapere prima di vivere la tua esperienza in Atelier.",
              "Everything you wish to know before living your experience at the Atelier."
            )}
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div>
            {FAQS.map((item, i) => (
              <FaqItem
                key={i}
                item={item}
                index={i}
                lang={lang}
                open={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
              />
            ))}
            <div className="border-t border-white/12" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
